import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTrophy, FaPlus, FaTrash, FaClock, FaUsers, FaCrown, FaCheck, FaEdit, FaTimes,
  FaFlagCheckered, FaMoneyBillWave, FaEye, FaSync, FaCoins
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const EMPTY_FORM = {
  nom: '',
  description: '',
  start_date: '',
  end_date: '',
  prize: '',
  prize_coins: '500',
  prize_money: '',
  max_participants: '',
  premium: true,
  entry_fee: '0',
};

// Convertit un tournoi API → valeurs du formulaire
const toForm = (t) => ({
  nom:              t.nom || '',
  description:      t.description || '',
  start_date:       t.startsAt ? t.startsAt.slice(0, 16) : '',
  end_date:         t.endsAt   ? t.endsAt.slice(0, 16)   : '',
  prize:            t.prix?.[0]?.label || '',
  prize_coins:      String(t.prix?.[0]?.coins ?? 500),
  prize_money:      t.prize_money || '',
  max_participants: t.maxParticipants ? String(t.maxParticipants) : '',
  premium:          t.premium ?? true,
  entry_fee:        String(t.entry_fee ?? 0),
});

function StatusBadge({ status }) {
  const map = {
    actif:   { label: 'Actif',   bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    bientot: { label: 'À venir', bg: 'bg-blue-500/20',  text: 'text-blue-400',  border: 'border-blue-500/30'  },
    termine: { label: 'Terminé', bg: 'bg-gray-500/20',  text: 'text-gray-400',  border: 'border-gray-500/30'  },
  };
  const s = map[status] || map.bientot;
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full border font-bold uppercase ${s.bg} ${s.text} ${s.border}`}
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
    >
      {s.label}
    </span>
  );
}

const MEDAL = ['🥇', '🥈', '🥉'];
const MEDAL_COLOR = ['#FFB81C', '#94a3b8', '#cd7f32'];

function ParticipantsModal({ tournament, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [lastRefresh, setLastRefresh]   = useState(null);
  const timerRef                        = useRef(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get(`/tournaments/${tournament._id}/leaderboard`)
      .then(({ data }) => {
        setParticipants(data.leaderboard || []);
        setLastRefresh(new Date());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tournament._id]);

  useEffect(() => {
    refresh();
    // Auto-refresh toutes les 30 secondes
    timerRef.current = setInterval(refresh, 30000);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  const nb           = participants.length;
  const entryFee     = parseInt(tournament.entry_fee) || 0;
  const totalPieces  = nb * entryFee;
  const top3         = participants.slice(0, 3);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)' }} onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full rounded-2xl flex flex-col"
        style={{ background: '#001a4d', border: '1px solid rgba(255,184,28,0.25)', maxWidth: 560, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <p className="text-qfl-gold font-black text-base uppercase"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>{tournament.nom}</p>
            <p className="text-white/40 text-xs mt-0.5">
              {lastRefresh && `Mis à jour à ${lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} disabled={loading}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/40 hover:text-qfl-gold disabled:opacity-30"
              title="Rafraîchir">
              <FaSync size={13} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white">
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-3 gap-3 px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-center">
            <div className="font-black text-2xl text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{nb}</div>
            <div className="text-white/35 text-xs uppercase tracking-wider">Inscrits</div>
          </div>
          {entryFee > 0 && (
            <>
              <div className="text-center">
                <div className="font-black text-2xl text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  🪙 {totalPieces.toLocaleString()}
                </div>
                <div className="text-white/35 text-xs uppercase tracking-wider">Pièces collectées</div>
              </div>
              <div className="text-center">
                <div className="font-black text-xl text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {(totalPieces / 1000 * 500).toLocaleString()} FCFA
                </div>
                <div className="text-white/35 text-xs uppercase tracking-wider">Valeur cagnotte</div>
              </div>
            </>
          )}
          {!entryFee && (
            <div className="col-span-2 flex items-center justify-center text-white/25 text-xs">Tournoi gratuit</div>
          )}
        </div>

        {/* Top 3 podium */}
        {top3.length >= 1 && (
          <div className="px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-white/30 text-xs uppercase tracking-wider mb-3">🏆 Classement actuel</p>
            <div className="flex flex-col gap-2">
              {top3.map((p, i) => (
                <div key={p.user_id} className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                  style={{
                    background: i === 0 ? 'rgba(255,184,28,0.1)' : i === 1 ? 'rgba(148,163,184,0.07)' : 'rgba(205,127,50,0.07)',
                    border: `1px solid ${i === 0 ? 'rgba(255,184,28,0.3)' : i === 1 ? 'rgba(148,163,184,0.2)' : 'rgba(205,127,50,0.2)'}`,
                  }}>
                  <span className="text-xl">{MEDAL[i]}</span>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                    style={{ background: `${MEDAL_COLOR[i]}22`, color: MEDAL_COLOR[i], border: `1px solid ${MEDAL_COLOR[i]}44` }}>
                    {p.pseudo?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-white truncate" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {p.pseudo}
                    </p>
                    <p className="text-white/30 text-xs">Niv. {p.level}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-base" style={{ color: MEDAL_COLOR[i], fontFamily: "'Rajdhani', sans-serif" }}>
                      {Number(p.score).toLocaleString()} pts
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liste complète */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {loading && participants.length === 0 ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : participants.length === 0 ? (
            <div className="py-10 text-center text-white/30 text-sm">Aucun inscrit pour le moment</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <th className="px-5 py-2 text-left text-white/30 text-xs uppercase tracking-wider font-bold">#</th>
                  <th className="px-3 py-2 text-left text-white/30 text-xs uppercase tracking-wider font-bold">Joueur</th>
                  <th className="px-3 py-2 text-right text-white/30 text-xs uppercase tracking-wider font-bold">Score</th>
                  <th className="px-5 py-2 text-right text-white/30 text-xs uppercase tracking-wider font-bold">Inscrit le</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p, i) => (
                  <tr key={p.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    className="hover:bg-white/5 transition-colors">
                    <td className="px-5 py-2.5">
                      <span className="font-black text-sm" style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        color: i < 3 ? MEDAL_COLOR[i] : 'rgba(255,255,255,0.25)',
                      }}>
                        {i < 3 ? MEDAL[i] : i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {p.pseudo}
                      </div>
                      <div className="text-white/25 text-xs">Niv. {p.level}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="font-black text-sm" style={{
                        color: p.score > 0 ? '#FFB81C' : 'rgba(255,255,255,0.25)',
                        fontFamily: "'Rajdhani', sans-serif",
                      }}>
                        {p.score > 0 ? `${Number(p.score).toLocaleString()} pts` : 'N\'a pas encore joué'}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-right text-white/25 text-xs">
                      {p.joined_at ? new Date(p.joined_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 flex-shrink-0 text-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-white/20 text-xs">🔄 Rafraîchissement automatique toutes les 30 secondes</p>
        </div>
      </motion.div>
    </div>
  );
}

function AdminTournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);   // null = création, sinon id du tournoi
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(null);
  const [closing, setClosing]         = useState(null);
  const [viewingParticipants, setViewingParticipants] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/tournaments')
      .then(({ data }) => setTournaments(data.tournaments || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t._id);
    setForm(toForm(t));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.start_date || !form.end_date) {
      toast.error('Nom, date de début et date de fin sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/tournaments/${editingId}`, form);
        toast.success('Tournoi mis à jour !');
      } else {
        await api.post('/tournaments', form);
        toast.success('Tournoi créé !');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (id, nom) => {
    if (!window.confirm(`Clôturer "${nom}" maintenant et distribuer les prix au top 3 ?`)) return;
    setClosing(id);
    try {
      const { data } = await api.post(`/tournaments/${id}/close`);
      toast.success(data.message || 'Tournoi clôturé !');
      if (data.winners?.length) {
        data.winners.forEach((w) =>
          toast(`🏆 ${w.pseudo} — ${w.coins} pièces (rang ${w.rank})`, { icon: '🎖️', duration: 4000 })
        );
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la clôture');
    } finally {
      setClosing(null);
    }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer le tournoi "${nom}" ?`)) return;
    setDeleting(id);
    try {
      await api.delete(`/tournaments/${id}`);
      toast.success('Tournoi supprimé');
      setTournaments((prev) => prev.filter((t) => t._id !== id));
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-2xl font-black text-qfl-gold uppercase tracking-widest"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            <FaTrophy className="inline mr-2 mb-1" />
            TOURNOIS
          </h2>
          <p className="text-white/50 text-sm mt-1">{tournaments.length} tournoi(s) en cours ou à venir</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-qfl-gold text-qfl-dark font-bold text-sm transition-opacity hover:opacity-80"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          <FaPlus size={14} />
          NOUVEAU
        </button>
      </div>

      {/* Formulaire création / édition */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-6"
          style={{ borderColor: 'rgba(255,184,28,0.3)', border: '1px solid rgba(255,184,28,0.3)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-qfl-gold font-bold uppercase tracking-widest text-sm"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {editingId ? 'Modifier le tournoi' : 'Créer un tournoi'}
            </h3>
            <button onClick={closeForm} className="text-white/40 hover:text-white/70 transition-colors">
              <FaTimes size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">Nom du tournoi *</label>
              <input name="nom" value={form.nom} onChange={handleChange} placeholder="Ex: Championnat Quotidien" className="form-input w-full" required />
            </div>

            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">Description</label>
              <input name="description" value={form.description} onChange={handleChange} placeholder="Ex: Top 10 du jour remportent des prix" className="form-input w-full" />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block">Date de début *</label>
              <input type="datetime-local" name="start_date" value={form.start_date} onChange={handleChange} className="form-input w-full" required />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Date de fin *</label>
              <input type="datetime-local" name="end_date" value={form.end_date} onChange={handleChange} className="form-input w-full" required />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block">Label récompense 1ère place</label>
              <input name="prize" value={form.prize} onChange={handleChange} placeholder="Ex: Badge Champion + pièces" className="form-input w-full" />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Pièces 1ère place (2e=60%, 3e=30%)</label>
              <input type="number" name="prize_coins" value={form.prize_coins} onChange={handleChange} placeholder="500" className="form-input w-full" min="0" />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block flex items-center gap-1">
                <FaMoneyBillWave size={11} className="text-green-400" /> Prize argent réel <span className="text-white/30">(optionnel)</span>
              </label>
              <input type="text" name="prize_money" value={form.prize_money} onChange={handleChange} placeholder="Ex: 10 000 FCFA" className="form-input w-full" />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block">Max participants</label>
              <input type="number" name="max_participants" value={form.max_participants} onChange={handleChange} placeholder="Vide = illimité" className="form-input w-full" min="1" />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block flex items-center gap-1">
                🪙 Frais d'entrée (pièces) <span className="text-white/30">(0 = gratuit)</span>
              </label>
              <input type="number" name="entry_fee" value={form.entry_fee} onChange={handleChange} placeholder="0" className="form-input w-full" min="0" />
              <p className="text-white/25 text-xs mt-1">Suggéré : 50 Bronze · 150 Silver · 500 Gold · 1000 Elite</p>
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div
                  onClick={() => setForm((f) => ({ ...f, premium: !f.premium }))}
                  className={`w-12 h-6 rounded-full relative transition-colors ${form.premium ? 'bg-qfl-gold' : 'bg-white/20'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.premium ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <FaCrown className="text-qfl-gold" size={12} />
                  Réservé Premium
                </span>
              </label>
            </div>

            <div className="md:col-span-2 flex gap-3 justify-end mt-2">
              <button
                type="button"
                onClick={closeForm}
                className="px-4 py-2 rounded-xl bg-white/10 text-white/60 text-sm font-bold hover:bg-white/20 transition-colors"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-qfl-gold text-qfl-dark font-bold text-sm hover:opacity-80 transition-opacity disabled:opacity-50"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-qfl-dark border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaCheck size={14} />
                )}
                {editingId ? 'ENREGISTRER' : 'CRÉER LE TOURNOI'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : tournaments.length === 0 ? (
        <div className="text-center py-16">
          <FaTrophy size={40} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">Aucun tournoi actif. Créez-en un !</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {tournaments.map((t, i) => (
            <motion.div
              key={t._id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="card flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-xl bg-qfl-gold/10 border border-qfl-gold/20 flex items-center justify-center flex-shrink-0">
                <FaTrophy size={16} className="text-qfl-gold" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-bold text-white text-sm truncate" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {t.nom}
                  </span>
                  <StatusBadge status={t.statut} />
                  {t.premium && <FaCrown size={12} className="text-qfl-gold flex-shrink-0" />}
                </div>
                <div className="flex items-center gap-3 text-white/40 text-xs flex-wrap">
                  <span className="flex items-center gap-1">
                    <FaUsers size={9} />
                    {t.participants} inscrits{t.maxParticipants ? ` / ${t.maxParticipants}` : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaClock size={9} />
                    fin {new Date(t.endsAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {t.prize_money && (
                    <span className="flex items-center gap-1 text-green-400">
                      <FaMoneyBillWave size={9} /> {t.prize_money}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Voir les inscrits */}
                <button
                  onClick={() => setViewingParticipants(t)}
                  className="w-9 h-9 rounded-lg bg-qfl-gold/10 border border-qfl-gold/20 flex items-center justify-center text-qfl-gold hover:bg-qfl-gold/20 transition-colors"
                  title="Voir les inscrits"
                >
                  <FaEye size={12} />
                </button>

                {/* Clôturer & distribuer (seulement si pas terminé) */}
                {t.statut !== 'termine' && (
                  <button
                    onClick={() => handleClose(t._id, t.nom)}
                    disabled={closing === t._id}
                    className="w-9 h-9 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                    title="Clôturer et distribuer les prix"
                  >
                    {closing === t._id ? (
                      <div className="w-3.5 h-3.5 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaFlagCheckered size={12} />
                    )}
                  </button>
                )}

                {/* Modifier */}
                <button
                  onClick={() => openEdit(t)}
                  className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 hover:bg-blue-500/20 transition-colors"
                  title="Modifier"
                >
                  <FaEdit size={13} />
                </button>

                {/* Supprimer */}
                <button
                  onClick={() => handleDelete(t._id, t.nom)}
                  disabled={deleting === t._id}
                  className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  title="Supprimer"
                >
                  {deleting === t._id ? (
                    <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaTrash size={12} />
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modale inscrits */}
      <AnimatePresence>
        {viewingParticipants && (
          <ParticipantsModal
            tournament={viewingParticipants}
            onClose={() => setViewingParticipants(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminTournaments;

